import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";
import { typeShell } from "../styles/typeShell";

const SIZE_CLASS = {
    small: "bp-md:max-w-md bp-lg:max-w-lg",
    medium: "bp-md:max-w-xl bp-lg:max-w-[40rem]",
    large: "bp-md:max-w-3xl bp-lg:max-w-4xl",
};

const ALIGN_CLASS = {
    center: "text-center",
    left: "text-left",
};

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    size = "medium",
    align = "center",
    showCloseButton = false,
    closeOnBackdropClick = false,
    closeOnEscape = true,
    actions = [],
    className = "",
    contentClassName = "",
}) => {
    const titleId = useId();

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e) => {
            if (e.key === "Escape" && closeOnEscape) {
                onClose();
            }
        };

        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // rAF で遅延させることで ResizeObserver ループエラーを防ぐ
        const rafId = requestAnimationFrame(() => {
            document.body.style.overflow = "hidden";
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = `-${scrollX}px`;
            document.body.style.width = "100%";
            document.documentElement.style.overflow = "hidden";
        });

        document.addEventListener("keydown", handleEscape);

        return () => {
            cancelAnimationFrame(rafId);
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "";
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.width = "";
            document.documentElement.style.overflow = "";
            window.scrollTo(scrollX, scrollY);
        };
    }, [isOpen, onClose, closeOnEscape]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && closeOnBackdropClick) {
            onClose();
        }
    };

    const handleActionClick = (action) => {
        if (action.onClick) {
            action.onClick();
        }
        if (action.closeOnClick !== false) {
            onClose();
        }
    };

    const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.medium;
    const alignClass = ALIGN_CLASS[align] || ALIGN_CLASS.center;

    const modalContent = (
        <div
            className="pointer-events-auto fixed inset-0 z-[999999] box-border flex h-screen w-screen animate-modal-fade-in items-center justify-center overflow-y-auto bg-black/50 p-2 bp-md:p-6 bp-lg:p-8"
            onClick={handleBackdropClick}
        >
            <div
                className={`relative z-[1000000] m-0 flex w-full max-h-[calc(100vh-2rem)] max-w-full animate-modal-slide-in flex-col overflow-hidden rounded-lg border border-solid border-border bg-surface p-0 shadow-none box-border bp-md:mx-auto bp-md:max-h-[calc(100vh-3rem)] bp-md:rounded-xl bp-lg:max-h-[calc(100vh-4rem)] bp-lg:rounded-2xl ${sizeClass} ${className}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
            >
                <div className="mb-0 box-border flex min-h-8 shrink-0 items-center justify-between border-0 border-b border-solid border-border px-modal py-1.5">
                    {title && (
                        <h2
                            id={titleId}
                            className={`m-0 mx-auto flex flex-1 shrink-0 items-center justify-center gap-2 text-center ${typeShell.bodySemibold}`}
                        >
                            {title}
                        </h2>
                    )}
                    {showCloseButton && (
                        <button
                            className="ml-2 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0.5 text-text-primary shadow-none transition-colors hover:text-text-secondary"
                            onClick={onClose}
                            aria-label="閉じる"
                            type="button"
                        >
                            <span className="material-symbols-outlined text-icon-control">
                                close
                            </span>
                        </button>
                    )}
                </div>

                {children && (
                    <div
                        className={`box-border min-h-0 max-h-[calc(100vh-8rem)] flex-1 overflow-y-auto text-text-primary bp-md:max-h-[calc(100vh-12rem)] bp-lg:max-h-[calc(100vh-14rem)] ${alignClass} ${contentClassName || "p-modal"}`}
                    >
                        {children}
                    </div>
                )}

                {actions.length > 0 && (
                    <div className="mt-0 box-border flex shrink-0 flex-col justify-between gap-2 border-0 border-t border-solid border-border px-modal py-2.5 bp-md:flex-row bp-md:gap-4 bp-lg:py-3">
                        {actions.map((action, index) => (
                            <Button
                                key={index}
                                variant={action.variant || "secondary"}
                                size={action.size || "lg"}
                                onClick={() => handleActionClick(action)}
                                disabled={action.disabled}
                                className="w-full min-w-20 bp-md:w-auto"
                            >
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default Modal;
